import React, { useRef, useState, useMemo, useEffect } from "react";
import { ScanFields as ScanFieldsType } from "../types";
import { Line } from "../types";
import { useSubinventoryLov } from "../hooks/useSubinventoryLov";
import { useRadSites } from "../hooks/useRadSites";
import { usePalletDetails } from "../hooks/usePalletDetails";
import { useCageDetails } from "../hooks/useCageDetails";
import { useToteDetails } from "../hooks/useToteDetails";
import { useLocalStorage } from "../hooks/useLocalStorage";
import PalletDropdown from "./PalletDropdown";
import CageDropdown from "./CageDropdown";
import ToteDropdown from "./ToteDropdown";

interface ScanFieldsProps {
  scanFields: ScanFieldsType;
  onScanFieldsChange: (fields: ScanFieldsType) => void;
  onSerialNumberScan: (serialNumber: string) => void;
  isDarkTheme?: boolean;
  selectedHeaderToOrg?: string;
  lines?: Line[];
  clearSerialNotFoundRef?: React.MutableRefObject<(() => void) | null>;
  serialScanLoading?: boolean;
  scanSerialError?: string | null;
  onFieldClickWithoutHeader?: () => void;
  radSites?: any[];
  selectedHeader?: any; // Add selectedHeader prop
}

const ScanFields: React.FC<ScanFieldsProps> = ({
  scanFields,
  onScanFieldsChange,
  onSerialNumberScan,
  isDarkTheme,
  selectedHeaderToOrg,
  lines = [],
  clearSerialNotFoundRef,
  serialScanLoading = false,
  scanSerialError,
  onFieldClickWithoutHeader,
  radSites: propRadSites = [],
  selectedHeader,
}) => {
  const { data: dynamicSubinventories, refetch: refetchSubinventories } = useSubinventoryLov(selectedHeaderToOrg, false);
  const subinventories = dynamicSubinventories;
  const { data: radSites, fetchRadSites } = useRadSites();
  const { refetch: refetchPalletDetails } = usePalletDetails();
  const { refetch: refetchCageDetails } = useCageDetails();
  const { refetch: refetchToteDetails } = useToteDetails();
  const [cachedToRad, setCachedToRad] = useLocalStorage<string>('toRadSelection', '');

  // Use prop radSites if available, otherwise use hook radSites
  const availableRadSites = propRadSites.length > 0 ? propRadSites : radSites;

  // Check if all fields should be disabled
  const allFieldsDisabled = useMemo(() => {
    if (!selectedHeader || !lines.length) return false;
    
    const allLinesSuccess = lines.every(line => line.status === 'Success');
    const headerApproved = selectedHeader.status === 'Approved';
    
    return allLinesSuccess && headerApproved;
  }, [selectedHeader, lines]);

  // Initialize TO RAD from cache on component mount
  useEffect(() => {
    if (cachedToRad && !scanFields.toRad) {
      onScanFieldsChange({ ...scanFields, toRad: cachedToRad });
    }
  }, [cachedToRad, scanFields.toRad, onScanFieldsChange]);

  // Debug: Log radSites data
  useEffect(() => {
    console.log('ScanFields - availableRadSites:', availableRadSites);
  }, [availableRadSites]);

  // Get the organizationId of selected ToRAD
  const getToRadOrgId = useMemo(() => {
    if (!scanFields.toRad) {
      return selectedHeaderToOrg;
    }
    
    const toRadSite = availableRadSites.find(site => site.organizationName === scanFields.toRad);
    return toRadSite?.organizationId || selectedHeaderToOrg;
  }, [scanFields.toRad, availableRadSites, selectedHeaderToOrg]);

  // Auto-fetch pallet, cage, and tote data when ToRAD changes or header is selected
  useEffect(() => {
    if (getToRadOrgId && scanFields.toRad && availableRadSites.length > 0 && selectedHeaderToOrg) {
      console.log('Auto-fetching data for ToRAD organizationId:', getToRadOrgId);
      refetchPalletDetails(getToRadOrgId, undefined, undefined, selectedHeader?.type?.toUpperCase());
      refetchCageDetails(getToRadOrgId);
      refetchToteDetails(getToRadOrgId);
    }
  }, [getToRadOrgId, scanFields.toRad, refetchPalletDetails, refetchCageDetails, refetchToteDetails, availableRadSites.length, selectedHeaderToOrg]);

  const [showRadDropdown, setShowRadDropdown] = useState(false);
  const [radSearch, setRadSearch] = useState('');
  const radDropdownRef = useRef<HTMLDivElement>(null);
  const trackingInputRef = useRef<HTMLInputElement>(null);
  const serialInputRef = useRef<HTMLInputElement>(null);
  const subinventoryDropdownRef = useRef<HTMLDivElement>(null);
  const [showSubinventoryDropdown, setShowSubinventoryDropdown] = useState(false);
  const [subinventorySearch, setSubinventorySearch] = useState('');
  const [selectedSubinventoryIndex, setSelectedSubinventoryIndex] = useState(-1);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [serialNotFoundMessage, setSerialNotFoundMessage] = useState('');
  const [dropdownJustOpened, setDropdownJustOpened] = useState(false);
  const [serialDebounceTimer, setSerialDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const [selectedRadIndex, setSelectedRadIndex] = useState(-1);
  const [focusedFields, setFocusedFields] = useState<Record<string, boolean>>({});
  
  const filteredRadSites = useMemo(() => {
    if (!radSearch) return availableRadSites;
    return availableRadSites.filter((site) =>
      site.organizationName.toLowerCase().includes(radSearch.toLowerCase())
    );
  }, [availableRadSites, radSearch]);

  const filteredSubinventories = useMemo(() => {
    if (!subinventorySearch) return subinventories;
    return subinventories.filter((subinv) =>
      subinv.toLowerCase().includes(subinventorySearch.toLowerCase())
    );
  }, [subinventories, subinventorySearch]);

  const handleSubinventoryKeyDown = (e: React.KeyboardEvent) => {
    if (!showSubinventoryDropdown || filteredSubinventories.length === 0) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (dropdownJustOpened) {
        setDropdownJustOpened(false);
        return;
      }
      setSelectedSubinventoryIndex(prev => {
        const newIndex = prev < filteredSubinventories.length - 1 ? prev + 1 : 0;
        setTimeout(() => {
          const dropdown = subinventoryDropdownRef.current;
          const selectedItem = dropdown?.children[newIndex] as HTMLElement;
          selectedItem?.scrollIntoView({ block: 'nearest' });
        }, 0);
        return newIndex;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSubinventoryIndex(prev => {
        const newIndex = prev > 0 ? prev - 1 : filteredSubinventories.length - 1;
        setTimeout(() => {
          const dropdown = subinventoryDropdownRef.current;
          const selectedItem = dropdown?.children[newIndex] as HTMLElement;
          selectedItem?.scrollIntoView({ block: 'nearest' });
        }, 0);
        return newIndex;
      });
    } else if (e.key === 'Enter') {
      if (selectedSubinventoryIndex >= 0) {
        e.preventDefault();
        const selectedSubinventory = filteredSubinventories[selectedSubinventoryIndex];
        onScanFieldsChange({
          ...scanFields,
          toSubinventory: selectedSubinventory,
          toLocator: "",
          scanSerialNumber: fieldErrors.toSubinventory ? "" : scanFields.scanSerialNumber,
        });
        if (fieldErrors.toSubinventory) {
          setFieldErrors(prev => ({ ...prev, toSubinventory: '' }));
        }
        setShowSubinventoryDropdown(false);
        setSubinventorySearch('');
        setSelectedSubinventoryIndex(-1);
      }
    } else if (e.key === 'Escape') {
      setShowSubinventoryDropdown(false);
      setSubinventorySearch('');
      setSelectedSubinventoryIndex(-1);
    }
  };
  
  // Expose clear function to parent via ref
  useEffect(() => {
    if (clearSerialNotFoundRef) {
      clearSerialNotFoundRef.current = () => setSerialNotFoundMessage('');
    }
  }, [clearSerialNotFoundRef]);
  
  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (serialDebounceTimer) {
        clearTimeout(serialDebounceTimer);
      }
    };
  }, [serialDebounceTimer]);
  
  const validateMandatoryFields = (serialNumber: string): boolean => {
    const errors: Record<string, string> = {};
    
    if (!scanFields.toRad || scanFields.toRad.trim() === '') {
      errors.toRad = 'To RAD Site is mandatory and cannot be null';
    }
    
    if (!scanFields.toSubinventory || scanFields.toSubinventory.trim() === '') {
      errors.toSubinventory = 'To Subinventory is mandatory and cannot be null';
    }
    
    if (!scanFields.trackingNumber || scanFields.trackingNumber.trim() === '') {
      errors.scanSerialNumber = 'Tracking Number is required';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSerialNumberChange = (value: string) => {
    // Check if required fields are filled before allowing serial number entry
    if (value.trim() && (!scanFields.toRad || !scanFields.pallet || !scanFields.toSubinventory || !scanFields.cage || !scanFields.tote)) {
      setFieldErrors(prev => ({ ...prev, scanSerialNumber: 'To RAD Site, Pallet, Subinventory, Cage and Tote is required' }));
      return;
    }
    
    const updatedFields = { ...scanFields, scanSerialNumber: value };
    onScanFieldsChange(updatedFields);
    if (serialNotFoundMessage) {
      setSerialNotFoundMessage('');
    }
    if (fieldErrors.scanSerialNumber) {
      setFieldErrors(prev => ({ ...prev, scanSerialNumber: '' }));
    }
    
    // Clear existing timer
    if (serialDebounceTimer) {
      clearTimeout(serialDebounceTimer);
    }
    
    // Auto-trigger validation with 2-second delay only if value is entered
    if (value.trim()) {
      // Check tracking number when user enters serial number
      if (!scanFields.trackingNumber || scanFields.trackingNumber.trim() === '') {
        setFieldErrors(prev => ({ ...prev, scanSerialNumber: 'Tracking Number is required' }));
        return;
      }
      
      const timer = setTimeout(() => {
        if (validateMandatoryFields(value.trim())) {
          onSerialNumberScan(value.trim());
        }
      }, 2000);
      setSerialDebounceTimer(timer);
    } else {
      setSerialDebounceTimer(null);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && scanFields.scanSerialNumber.trim()) {
      // Check if required fields are filled
      if (!scanFields.toRad || !scanFields.pallet || !scanFields.toSubinventory || !scanFields.cage || !scanFields.tote) {
        setFieldErrors(prev => ({ ...prev, scanSerialNumber: 'To RAD Site, Pallet, Subinventory, Cage and Tote is required' }));
        return;
      }
      
      // Check tracking number when user presses Enter
      if (!scanFields.trackingNumber || scanFields.trackingNumber.trim() === '') {
        setFieldErrors(prev => ({ ...prev, scanSerialNumber: 'Tracking Number is required' }));
        return;
      }
      
      // Clear debounce timer and trigger immediately on Enter
      if (serialDebounceTimer) {
        clearTimeout(serialDebounceTimer);
        setSerialDebounceTimer(null);
      }
      
      if (validateMandatoryFields(scanFields.scanSerialNumber.trim())) {
        onSerialNumberScan(scanFields.scanSerialNumber.trim());
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        subinventoryDropdownRef.current &&
        !subinventoryDropdownRef.current.contains(event.target as Node)
      ) {
        setShowSubinventoryDropdown(false);
        setSubinventorySearch('');
      }
      if (
        radDropdownRef.current &&
        !radDropdownRef.current.contains(event.target as Node)
      ) {
        setShowRadDropdown(false);
        setRadSearch('');
      }
    };

    if (showSubinventoryDropdown || showRadDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSubinventoryDropdown, showRadDropdown]);

  return (
    <div
      style={{
        marginBottom: "10px",
        padding: "15px",
        border: `1px solid ${isDarkTheme ? "#555" : "#ccc"}`,
        borderRadius: "4px",
        backgroundColor: allFieldsDisabled ? (isDarkTheme ? "#1a1a1a" : "#f5f5f5") : (isDarkTheme ? "#2a2a2a" : "#fff"),
        width: "fit-content",
        opacity: allFieldsDisabled ? 0.6 : 1,
        pointerEvents: allFieldsDisabled ? "none" : "auto",
      }}
    >
      <div style={{ display: "flex", gap: "15px", alignItems: "flex-start" }}>
        <div style={{ position: "relative", display: "flex", flexDirection: "column" }} data-tour="torad-field">
          <div style={{ position: "relative" }}>
            {(focusedFields.toRad || scanFields.toRad) && (
              <label style={{
                position: "absolute",
                top: "-8px",
                left: "8px",
                backgroundColor: isDarkTheme ? "#2a2a2a" : "#fff",
                padding: "0 4px",
                fontSize: "12px",
                color: isDarkTheme ? "#fff" : "#000",
                zIndex: 1
              }}>
                To RAD
              </label>
            )}
            <input
              type="text"
              value={radSearch || scanFields.toRad}
              onChange={(e) => {
                const value = e.target.value;
                setRadSearch(value);
                
                // Clear all scan fields if ToRAD is changed or cleared
                if (scanFields.toRad !== value) {
                  onScanFieldsChange({ 
                    ...scanFields, 
                    toRad: value,
                    pallet: "",
                    toSubinventory: "",
                    cage: "",
                    tote: "",
                    toLocator: "",
                    trackingNumber: "",
                    scanSerialNumber: ""
                  });
                } else {
                  onScanFieldsChange({ ...scanFields, toRad: value });
                }
                
                if (value.trim()) {
                  setShowRadDropdown(true);
                } else {
                  setShowRadDropdown(false);
                }
              }}
              onFocus={() => {
                if (!selectedHeaderToOrg && onFieldClickWithoutHeader) {
                  onFieldClickWithoutHeader();
                  return;
                }
                setFocusedFields(prev => ({ ...prev, toRad: true }));
                setRadSearch(scanFields.toRad);
                setShowRadDropdown(true);
                setSelectedRadIndex(-1);
                // Fallback: fetch if availableRadSites is empty
                if (availableRadSites.length === 0) {
                  fetchRadSites();
                }
              }}
              onKeyDown={(e) => {
                if (!showRadDropdown || filteredRadSites.length === 0) return;
                
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setSelectedRadIndex(prev => {
                    const newIndex = prev < filteredRadSites.length - 1 ? prev + 1 : 0;
                    setTimeout(() => {
                      const dropdown = radDropdownRef.current;
                      const selectedItem = dropdown?.children[newIndex] as HTMLElement;
                      selectedItem?.scrollIntoView({ block: 'nearest' });
                    }, 0);
                    return newIndex;
                  });
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setSelectedRadIndex(prev => {
                    const newIndex = prev > 0 ? prev - 1 : filteredRadSites.length - 1;
                    setTimeout(() => {
                      const dropdown = radDropdownRef.current;
                      const selectedItem = dropdown?.children[newIndex] as HTMLElement;
                      selectedItem?.scrollIntoView({ block: 'nearest' });
                    }, 0);
                    return newIndex;
                  });
                } else if (e.key === 'Enter') {
                  if (selectedRadIndex >= 0) {
                    e.preventDefault();
                    const selectedRadSite = filteredRadSites[selectedRadIndex];
                    const selectedRadValue = selectedRadSite.organizationName;
                    onScanFieldsChange({ 
                      ...scanFields, 
                      toRad: selectedRadValue,
                      pallet: "",
                      toSubinventory: "",
                      cage: "",
                      tote: "",
                      toLocator: "",
                      trackingNumber: "",
                      scanSerialNumber: ""
                    });
                    setCachedToRad(selectedRadValue);
                    setFieldErrors(prev => ({
                      ...prev,
                      toRad: '',
                      pallet: '',
                      cage: '',
                      tote: '',
                      trackingNumber: '',
                      scanSerialNumber: '',
                      toSubinventory: ''
                    }));
                    setShowRadDropdown(false);
                    setRadSearch('');
                    setSelectedRadIndex(-1);
                  }
                } else if (e.key === 'Escape') {
                  setShowRadDropdown(false);
                  setRadSearch('');
                  setSelectedRadIndex(-1);
                }
              }}
              onBlur={() => {
                setFocusedFields(prev => ({ ...prev, toRad: false }));
              }}
              onClick={() => {
                if (!selectedHeaderToOrg && onFieldClickWithoutHeader) {
                  onFieldClickWithoutHeader();
                  return;
                }
                setRadSearch(scanFields.toRad);
                setShowRadDropdown(true);
                // Fallback: fetch if availableRadSites is empty
                if (availableRadSites.length === 0) {
                  fetchRadSites();
                }
              }}
              placeholder={focusedFields.toRad || scanFields.toRad ? "" : "To RAD... *"}
              style={{
                padding: "8px 30px 8px 8px",
                border: `1px solid ${isDarkTheme ? "#555" : "#ccc"}`,
                borderRadius: "4px",
                width: "100px",
                backgroundColor: isDarkTheme ? "#333" : "#fff",
                color: isDarkTheme ? "#fff" : "#000",
                boxSizing: "border-box",
                fontSize: "14px",
              }}
            />
            {scanFields.toRad ? (
              <span
                onClick={() => {
                  onScanFieldsChange({ 
                    ...scanFields, 
                    toRad: "",
                    pallet: "",
                    toSubinventory: "",
                    cage: "",
                    tote: "",
                    toLocator: "",
                    trackingNumber: "",
                    scanSerialNumber: ""
                  });
                  // Clear cached TO RAD when manually cleared
                  setCachedToRad('');
                  setShowRadDropdown(false);
                  setRadSearch('');
                }}
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "8px",
                  cursor: "pointer",
                  color: isDarkTheme ? "#ccc" : "#666",
                  fontSize: "20px",
                  fontWeight: "bold",
                  lineHeight: "1",
                  height: "20px",
                }}
              >
                ×
              </span>
            ) : (
              <span
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
              </span>
            )}
          </div>
          {showRadDropdown && filteredRadSites.length > 0 && (
            <div
              ref={radDropdownRef}
              style={{
                position: "absolute",
                top: "100%",
                left: "0",
                backgroundColor: isDarkTheme ? "#333" : "#fff",
                border: `1px solid ${isDarkTheme ? "#555" : "#ccc"}`,
                borderRadius: "4px",
                zIndex: 1000,
                width: "150px",
                maxHeight: "200px",
                overflowY: "auto",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              {filteredRadSites.map((radSite, index) => (
                <div
                  key={radSite.organizationId}
                  onClick={() => {
                    const selectedRadValue = radSite.organizationName;
                    onScanFieldsChange({ 
                      ...scanFields, 
                      toRad: selectedRadValue,
                      pallet: "",
                      toSubinventory: "",
                      cage: "",
                      tote: "",
                      toLocator: "",
                      trackingNumber: "",
                      scanSerialNumber: ""
                    });
                    // Cache the selected TO RAD value
                    setCachedToRad(selectedRadValue);
                    // Clear all To RAD related errors when To RAD is selected
                    setFieldErrors(prev => ({
                      ...prev,
                      toRad: '',
                      pallet: '',
                      cage: '',
                      tote: '',
                      trackingNumber: '',
                      scanSerialNumber: '',
                      toSubinventory: ''
                    }));
                    setShowRadDropdown(false);
                    setRadSearch('');
                    setSelectedRadIndex(-1);
                  }}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    backgroundColor: selectedRadIndex === index ? (isDarkTheme ? '#555' : '#e3f2fd') : "transparent",
                    color: isDarkTheme ? "#fff" : "#000",
                    borderBottom: `1px solid ${isDarkTheme ? "#555" : "#eee"}`,
                    fontSize: "12px",
                  }}
                  onMouseEnter={(e) => {
                    if (selectedRadIndex !== index) {
                      e.currentTarget.style.backgroundColor = isDarkTheme ? '#444' : '#f5f5f5';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedRadIndex !== index) {
                      e.currentTarget.style.backgroundColor = selectedRadIndex === index ? (isDarkTheme ? '#555' : '#e3f2fd') : 'transparent';
                    }
                  }}
                >
                  {radSite.organizationName}
                </div>
              ))}
            </div>
          )}
          {fieldErrors.toRad && (
            <div style={{
              color: '#dc3545',
              fontSize: '11px',
              marginTop: '2px',
              minHeight: '14px'
            }}>
              {fieldErrors.toRad}
            </div>
          )}
        </div>
        <div style={{ position: "relative", display: "flex", flexDirection: "column" }} data-tour="pallet-field">
          {(scanFields.pallet) && (
            <label style={{
              position: "absolute",
              top: "-8px",
              left: "8px",
              backgroundColor: isDarkTheme ? "#2a2a2a" : "#fff",
              padding: "0 4px",
              fontSize: "12px",
              color: isDarkTheme ? "#fff" : "#000",
              zIndex: 1
            }}>
              Pallet
            </label>
          )}
          <PalletDropdown
            value={scanFields.pallet}
            onChange={(value, palletData) => {
              if (!selectedHeaderToOrg && onFieldClickWithoutHeader) {
                onFieldClickWithoutHeader();
                return;
              }
              onScanFieldsChange({ ...scanFields, pallet: value, toSubinventory: "", toLocator: "" });
              setFieldErrors(prev => ({ ...prev, cage: '', tote: '', trackingNumber: '', scanSerialNumber: '', toSubinventory: '' }));
              
              // Auto-populate subinventory if available
              if (getToRadOrgId && palletData?.subinventoryCode) {
                onScanFieldsChange({
                  ...scanFields,
                  pallet: value,
                  toSubinventory: palletData.subinventoryCode,
                  toLocator: ""
                });
              } else if (getToRadOrgId) {
                setTimeout(() => {
                  refetchSubinventories(getToRadOrgId);
                }, 100);
              }
            }}
            selectedHeaderToOrg={getToRadOrgId}
            isDarkTheme={isDarkTheme}
            width="260px"
            placeholder={scanFields.pallet ? "" : "Enter pallet"}
            onClear={() => {
              onScanFieldsChange({ 
                ...scanFields, 
                pallet: "", 
                toSubinventory: "", 
                cage: "", 
                tote: "",
                toLocator: ""
              });
              setFieldErrors(prev => ({ ...prev, pallet: '', cage: '', tote: '' }));
            }}
            onFieldClickWithoutHeader={onFieldClickWithoutHeader}
            onToRadRequired={() => setFieldErrors(prev => ({ ...prev, pallet: 'To RAD is required' }))}
            toRadValue={scanFields.toRad}
            orderType={selectedHeader?.type?.toUpperCase()}
          />
          {fieldErrors.pallet && (
            <div style={{
              color: '#dc3545',
              fontSize: '11px',
              marginTop: '2px',
              minHeight: '14px'
            }}>
              {fieldErrors.pallet}
            </div>
          )}
        </div>
        <div style={{ position: "relative", display: "flex", flexDirection: "column" }} data-tour="subinventory-field">
          <div style={{ position: "relative" }}>
            {(focusedFields.toSubinventory || scanFields.toSubinventory) && (
              <label style={{
                position: "absolute",
                top: "-8px",
                left: "8px",
                backgroundColor: isDarkTheme ? "#2a2a2a" : "#fff",
                padding: "0 4px",
                fontSize: "12px",
                color: isDarkTheme ? "#fff" : "#000",
                zIndex: 1
              }}>
                Subinventory
              </label>
            )}
            <input
              type="text"
              value={subinventorySearch || scanFields.toSubinventory}
              onChange={(e) => {
                const value = e.target.value;
                console.log('onChange triggered with value:', value);
                setSubinventorySearch(value);
                
                // Update parent state only when user selects from dropdown
                onScanFieldsChange({
                  ...scanFields,
                  toSubinventory: value,
                  toLocator: "",
                  scanSerialNumber: fieldErrors.toSubinventory ? "" : scanFields.scanSerialNumber,
                });
                if (fieldErrors.toSubinventory) {
                  setFieldErrors(prev => ({ ...prev, toSubinventory: '' }));
                }
                
                // Clear tracking/serial errors if all required fields are now filled
                if (scanFields.pallet && value && scanFields.cage && scanFields.tote) {
                  setFieldErrors(prev => ({ ...prev, trackingNumber: '', scanSerialNumber: '' }));
                }
                
                // Show dropdown and trigger API only if needed
                if (value.trim()) {
                  console.log('Showing dropdown for value:', value);
                  setShowSubinventoryDropdown(true);
                  setDropdownJustOpened(true);
                  setTimeout(() => setDropdownJustOpened(false), 200);
                  // Don't call API here since it's called from pallet selection
                } else {
                  setShowSubinventoryDropdown(false);
                }
              }}
              onBlur={() => {
                setFocusedFields(prev => ({ ...prev, toSubinventory: false }));
              }}
              onFocus={() => {
                if (!selectedHeaderToOrg && onFieldClickWithoutHeader) {
                  onFieldClickWithoutHeader();
                  return;
                }
                if (!scanFields.pallet || scanFields.pallet.trim() === '') {
                  setFieldErrors(prev => ({ ...prev, toSubinventory: 'Pallet is required' }));
                  return;
                }
                setFocusedFields(prev => ({ ...prev, toSubinventory: true }));
                setSubinventorySearch(scanFields.toSubinventory);
                if (scanFields.toSubinventory) {
                  setShowSubinventoryDropdown(true);
                }
                setSelectedSubinventoryIndex(-1);
              }}
              onKeyDown={handleSubinventoryKeyDown}
              onClick={() => {
                if (!selectedHeaderToOrg && onFieldClickWithoutHeader) {
                  onFieldClickWithoutHeader();
                  return;
                }
                if (!scanFields.pallet || scanFields.pallet.trim() === '') {
                  setFieldErrors(prev => ({ ...prev, toSubinventory: 'Pallet is required' }));
                  return;
                }
                setSubinventorySearch(scanFields.toSubinventory);
                setShowSubinventoryDropdown(true);
                setSelectedSubinventoryIndex(-1);
              }}
              disabled={true}
              placeholder={focusedFields.toSubinventory || scanFields.toSubinventory ? "" : "Search subinventory..."}
              style={{
                padding: "8px 30px 8px 8px",
                border: `1px solid ${isDarkTheme ? "#555" : "#ccc"}`,
                borderRadius: "4px",
                width: "150px",
                backgroundColor: isDarkTheme ? "#444" : "#f5f5f5",
                color: isDarkTheme ? "#fff" : "#000",
                boxSizing: "border-box",
                fontSize: "14px",
                cursor: "not-allowed"
              }}
            />
            {scanFields.toSubinventory ? (
              <span
                onClick={() => {
                  onScanFieldsChange({ ...scanFields, toSubinventory: "", toLocator: "", scanSerialNumber: "" });
                  setShowSubinventoryDropdown(false);
                  if (fieldErrors.toSubinventory) {
                    setFieldErrors(prev => ({ ...prev, toSubinventory: '' }));
                  }
                }}
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "8px",
                  cursor: "pointer",
                  color: isDarkTheme ? "#ccc" : "#666",
                  fontSize: "20px",
                  fontWeight: "bold",
                  lineHeight: "1",
                  height: "20px",
                }}
              >
                ×
              </span>
            ) : (
              <span
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
              </span>
            )}
          </div>
          {showSubinventoryDropdown && filteredSubinventories.length > 0 && (
            <div
              ref={subinventoryDropdownRef}
              style={{
                position: "absolute",
                top: "100%",
                left: "0",
                backgroundColor: isDarkTheme ? "#333" : "#fff",
                border: `1px solid ${isDarkTheme ? "#555" : "#ccc"}`,
                borderRadius: "4px",
                zIndex: 1000,
                width: "150px",
                maxHeight: "200px",
                overflowY: "auto",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              {filteredSubinventories.map((subinventory, index) => (
                <div
                  key={subinventory}
                  onClick={() => {
                    onScanFieldsChange({
                      ...scanFields,
                      toSubinventory: subinventory,
                      toLocator: "",
                      scanSerialNumber: fieldErrors.toSubinventory ? "" : scanFields.scanSerialNumber,
                    });
                    if (fieldErrors.toSubinventory) {
                      setFieldErrors(prev => ({ ...prev, toSubinventory: '' }));
                    }
                    setShowSubinventoryDropdown(false);
                    setSubinventorySearch('');
                    setSelectedSubinventoryIndex(-1);
                  }}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    backgroundColor: selectedSubinventoryIndex === index ? (isDarkTheme ? "#555" : "#e3f2fd") : "transparent",
                    color: isDarkTheme ? "#fff" : "#000",
                    borderBottom: `1px solid ${isDarkTheme ? "#555" : "#eee"}`,
                    fontSize: "12px",
                  }}
                  onMouseEnter={(e) => {
                    if (selectedSubinventoryIndex !== index) {
                      e.currentTarget.style.backgroundColor = isDarkTheme ? "#444" : "#f5f5f5";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedSubinventoryIndex !== index) {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                >
                  {subinventory}
                </div>
              ))}
            </div>
          )}
          {fieldErrors.toSubinventory && (
            <div style={{
              color: '#dc3545',
              fontSize: '11px',
              marginTop: '2px',
              minHeight: '14px'
            }}>
              {fieldErrors.toSubinventory}
            </div>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column" }} data-tour="cage-field">
          <div style={{ display: "flex", alignItems: "center", gap: "8px", position: "relative" }}>
            {(scanFields.cage) && (
              <label style={{
                position: "absolute",
                top: "-8px",
                left: "8px",
                backgroundColor: isDarkTheme ? "#2a2a2a" : "#fff",
                padding: "0 4px",
                fontSize: "12px",
                color: isDarkTheme ? "#fff" : "#000",
                zIndex: 1
              }}>
                Cage
              </label>
            )}
            <CageDropdown
              value={scanFields.cage}
              onChange={(value) => {
                if (!selectedHeaderToOrg && onFieldClickWithoutHeader) {
                  onFieldClickWithoutHeader();
                  return;
                }
                onScanFieldsChange({ ...scanFields, cage: value });
                setFieldErrors(prev => ({ ...prev, cage: '' }));
                // Clear tracking/serial errors if all required fields are now filled
                if (scanFields.pallet && scanFields.toSubinventory && value && scanFields.tote) {
                  setFieldErrors(prev => ({ ...prev, trackingNumber: '', scanSerialNumber: '' }));
                }
              }}
              selectedHeaderToOrg={getToRadOrgId}
              isDarkTheme={isDarkTheme}
              width="120px"
              placeholder={scanFields.cage ? "" : "Enter cage"}
              palletValue={scanFields.pallet}
              onError={(error) => setFieldErrors(prev => ({ ...prev, cage: error }))}
              onToRadRequired={() => setFieldErrors(prev => ({ ...prev, cage: 'To RAD is required' }))}
              toRadValue={scanFields.toRad}
              onFieldClickWithoutHeader={onFieldClickWithoutHeader}
            />
            <span
              onClick={() => {
                if (!scanFields.pallet || scanFields.pallet.trim() === '') {
                  setFieldErrors(prev => ({ ...prev, cage: 'Pallet is required' }));
                  return;
                }
                onScanFieldsChange({ ...scanFields, cage: "0" });
              }}
              style={{
                fontSize: "16px",
                color: isDarkTheme ? "#007bff" : "#0066cc",
                cursor: "pointer",
                fontWeight: "bold",
              }}
            >
              ⏭
            </span>
          </div>
          {fieldErrors.cage && (
            <div style={{
              color: '#dc3545',
              fontSize: '11px',
              marginTop: '2px',
              minHeight: '14px'
            }}>
              {fieldErrors.cage}
            </div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column" }} data-tour="tote-field">
          <div style={{ display: "flex", alignItems: "center", gap: "8px", position: "relative" }}>
            {(scanFields.tote) && (
              <label style={{
                position: "absolute",
                top: "-8px",
                left: "8px",
                backgroundColor: isDarkTheme ? "#2a2a2a" : "#fff",
                padding: "0 4px",
                fontSize: "12px",
                color: isDarkTheme ? "#fff" : "#000",
                zIndex: 1
              }}>
                Tote
              </label>
            )}
            <ToteDropdown
              value={scanFields.tote}
              onChange={(value) => {
                if (!selectedHeaderToOrg && onFieldClickWithoutHeader) {
                  onFieldClickWithoutHeader();
                  return;
                }
                onScanFieldsChange({ ...scanFields, tote: value });
                setFieldErrors(prev => ({ ...prev, tote: '' }));
                // Clear tracking/serial errors if all required fields are now filled
                if (scanFields.pallet && scanFields.toSubinventory && scanFields.cage && value) {
                  setFieldErrors(prev => ({ ...prev, trackingNumber: '', scanSerialNumber: '' }));
                }
              }}
              selectedHeaderToOrg={getToRadOrgId}
              isDarkTheme={isDarkTheme}
              width="120px"
              placeholder={scanFields.tote ? "" : "Enter tote"}
              palletValue={scanFields.pallet}
              onError={(error) => setFieldErrors(prev => ({ ...prev, tote: error }))}
              onToRadRequired={() => setFieldErrors(prev => ({ ...prev, tote: 'To RAD is required' }))}
              toRadValue={scanFields.toRad}
              onFieldClickWithoutHeader={onFieldClickWithoutHeader}
            />
            <span
              onClick={() => {
                if (!scanFields.pallet || scanFields.pallet.trim() === '') {
                  setFieldErrors(prev => ({ ...prev, tote: 'Pallet is required' }));
                  return;
                }
                onScanFieldsChange({ ...scanFields, tote: "0" });
                setTimeout(() => trackingInputRef.current?.focus(), 100);
              }}
              style={{
                fontSize: "16px",
                color: isDarkTheme ? "#007bff" : "#0066cc",
                cursor: "pointer",
                fontWeight: "bold",
              }}
            >
              ⏭
            </span>
          </div>
          {fieldErrors.tote && (
            <div style={{
              color: '#dc3545',
              fontSize: '11px',
              marginTop: '2px',
              minHeight: '14px'
            }}>
              {fieldErrors.tote}
            </div>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column" }} data-tour="tracking-field">
          <div style={{ position: "relative" }}>
            {(focusedFields.trackingNumber || scanFields.trackingNumber) && (
              <label style={{
                position: "absolute",
                top: "-8px",
                left: "8px",
                backgroundColor: isDarkTheme ? "#2a2a2a" : "#fff",
                padding: "0 4px",
                fontSize: "12px",
                color: isDarkTheme ? "#fff" : "#000",
                zIndex: 1
              }}>
                Tracking Number
              </label>
            )}
            <input
              ref={trackingInputRef}
              type="text"
              value={scanFields.trackingNumber}
              onChange={(e) => {
                if (!selectedHeaderToOrg && onFieldClickWithoutHeader) {
                  onFieldClickWithoutHeader();
                  return;
                }
                const value = e.target.value;
                // Check if required fields are filled before allowing tracking number entry
                if (value.trim() && (!scanFields.toRad || !scanFields.pallet || !scanFields.toSubinventory || !scanFields.cage || !scanFields.tote)) {
                  setFieldErrors(prev => ({ ...prev, trackingNumber: 'To RAD Site, Pallet, Subinventory, Cage and Tote is required' }));
                  return;
                }
                
                onScanFieldsChange({ ...scanFields, trackingNumber: value });
                if (fieldErrors.trackingNumber) {
                  setFieldErrors(prev => ({ ...prev, trackingNumber: '' }));
                }
              }}
              onFocus={() => {
                if (!selectedHeaderToOrg && onFieldClickWithoutHeader) {
                  onFieldClickWithoutHeader();
                  return;
                }
                if (!scanFields.toRad || scanFields.toRad.trim() === '') {
                  setFieldErrors(prev => ({ ...prev, trackingNumber: 'To RAD is required' }));
                  return;
                }
                setFocusedFields(prev => ({ ...prev, trackingNumber: true }));
              }}
              onBlur={() => {
                setFocusedFields(prev => ({ ...prev, trackingNumber: false }));
              }}
              placeholder={focusedFields.trackingNumber || scanFields.trackingNumber ? "" : "Enter tracking number *"}
              style={{
                padding: "8px",
                paddingRight: scanFields.trackingNumber ? "30px" : "8px",
                border: `2px solid ${fieldErrors.trackingNumber ? '#dc3545' : (isDarkTheme ? "#555" : "#ccc")}`,
                borderRadius: "4px",
                width: "200px",
                backgroundColor: isDarkTheme ? "#333" : "#fff",
                color: isDarkTheme ? "#fff" : "#000",
                boxSizing: "border-box",
              }}
            />
            {scanFields.trackingNumber && (
              <span
                onClick={() => {
                  onScanFieldsChange({ ...scanFields, trackingNumber: "" });
                  if (fieldErrors.trackingNumber) {
                    setFieldErrors(prev => ({ ...prev, trackingNumber: '' }));
                  }
                }}
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "8px",
                  cursor: "pointer",
                  color: isDarkTheme ? "#ccc" : "#666",
                  fontSize: "20px",
                  fontWeight: "bold",
                  lineHeight: "1",
                  height: "20px",
                }}
              >
                ×
              </span>
            )}
          </div>
          {fieldErrors.trackingNumber && (
            <div style={{
              color: '#dc3545',
              fontSize: '11px',
              marginTop: '2px',
              minHeight: '14px'
            }}>
              {fieldErrors.trackingNumber}
            </div>
          )}
        </div>
        <div style={{ position: "relative", display: "flex", flexDirection: "column" }} data-tour="serial-field">
          <div style={{ position: "relative" }}>
            {(focusedFields.scanSerialNumber || scanFields.scanSerialNumber) && (
              <label style={{
                position: "absolute",
                top: "-8px",
                left: "8px",
                backgroundColor: isDarkTheme ? "#2a2a2a" : "#fff",
                padding: "0 4px",
                fontSize: "12px",
                color: isDarkTheme ? "#fff" : "#000",
                zIndex: 1
              }}>
                Serial Number
              </label>
            )}
            <input
              ref={serialInputRef}
              type="text"
              value={scanFields.scanSerialNumber}
              onChange={(e) => handleSerialNumberChange(e.target.value)}
              onKeyPress={handleKeyPress}
              onFocus={() => {
                if (!selectedHeaderToOrg && onFieldClickWithoutHeader) {
                  onFieldClickWithoutHeader();
                  return;
                }
                if (!scanFields.toRad || scanFields.toRad.trim() === '') {
                  setFieldErrors(prev => ({ ...prev, scanSerialNumber: 'To RAD is required' }));
                  return;
                }
                setFocusedFields(prev => ({ ...prev, scanSerialNumber: true }));
              }}
              onBlur={() => {
                setFocusedFields(prev => ({ ...prev, scanSerialNumber: false }));
              }}
              placeholder={focusedFields.scanSerialNumber || scanFields.scanSerialNumber ? "" : "Scan or enter serial number"}
              style={{
                padding: "8px",
                paddingRight: scanFields.scanSerialNumber ? "30px" : "8px",
                border: `2px solid ${isDarkTheme ? "#555" : "#ccc"}`,
                borderRadius: "4px",
                width: "200px",
                backgroundColor: isDarkTheme ? "#333" : "#fff",
                color: isDarkTheme ? "#fff" : "#000",
                boxSizing: "border-box",
              }}
            />
            {scanFields.scanSerialNumber && !serialScanLoading && (
              <span
                onClick={() => {
                  const updatedFields = { ...scanFields, scanSerialNumber: "" };
                  onScanFieldsChange(updatedFields);
                  setSerialNotFoundMessage('');
                  setFieldErrors(prev => ({ ...prev, scanSerialNumber: '' }));
                }}
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "8px",
                  cursor: "pointer",
                  color: isDarkTheme ? "#ccc" : "#666",
                  fontSize: "20px",
                  fontWeight: "bold",
                  lineHeight: "1",
                  height: "20px",
                }}
              >
                ×
              </span>
            )}
            {serialScanLoading && (
              <>
                <style>
                  {`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}
                </style>
                <div
                  style={{
                    position: "absolute",
                    right: "8px",
                    top: "8px",
                    width: "20px",
                    height: "20px",
                    border: "2px solid #f3f3f3",
                    borderTop: "2px solid #007bff",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite"
                  }}
                />
              </>
            )}
          </div>
          {serialNotFoundMessage && (
            <div style={{
              color: '#dc3545',
              fontSize: '11px',
              marginTop: '2px',
              minHeight: '14px'
            }}>
              {serialNotFoundMessage}
            </div>
          )}
          {scanSerialError && (
            <div style={{
              color: '#dc3545',
              fontSize: '11px',
              marginTop: '2px',
              minHeight: '14px'
            }}>
              {scanSerialError}
            </div>
          )}
          {fieldErrors.scanSerialNumber && (
            <div style={{
              color: '#dc3545',
              fontSize: '11px',
              marginTop: '2px',
              minHeight: '14px'
            }}>
              {fieldErrors.scanSerialNumber}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScanFields;